import { useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useBookingBootstrap } from "./useBookingBootstrap";
import { parseGuestAppConfig } from "../../Helper/guestAppConfig";

export const useLandingV2Logic = () => {
  const [searchParams] = useSearchParams();
  const bID = searchParams.get("bID");
  // Deep-link: /go?bID=…&upsellID=… → bounce straight to the dedicated
  // /upsell page (skips landing CTA + onboarding when security is done).
  const upsellID = searchParams.get("upsellID");
  // Automatic pass-through (QR lookup, or a reloaded tab resuming): replace
  // this entry so Back from the next screen doesn't land here and bounce
  // straight forward again.
  const autoStart = searchParams.get("autoStart") === "1";
  const navigate = useNavigate();

  const {
    manager,
    loading,
    error,
    bookingDetails,
    isLinkExpired,
    currentUser,
  } = useBookingBootstrap(bID);

  const handleGetStarted = useCallback(() => {
    if (isLinkExpired) {
      console.log("Cannot proceed - link has expired");
      return;
    }

    if (
      !loading &&
      !error &&
      manager.name &&
      manager.email &&
      bookingDetails &&
      currentUser.status === "succeeded"
    ) {
      const ownerId = bookingDetails.room.owner
        ? bookingDetails.room.owner.split("::")[0]
        : null;

      const roomWithTimezone = {
        ...bookingDetails.room,
        owner: {
          id: ownerId,
          timezone: bookingDetails.owner?.timezone || "America/Los_Angeles",
        },
      };

      const accessTimes = bookingDetails.accessTimes || null;

      // Deep-link shortcut: if the URL carried an upsellID, send the
      // guest to the dedicated /upsell page instead of landing →
      // onboarding → main. That page reuses the same booking bootstrap
      // and opens the purchase UI directly when security is done.
      if (upsellID) {
        navigate(`/upsell?bID=${encodeURIComponent(bID)}&upsellID=${encodeURIComponent(upsellID)}`, {
          replace: true,
        });
        return;
      }

      const stepsDone = bookingDetails.securityStepsComplete === true;
      let onboardingComplete = false;
      try {
        const status =
          typeof bookingDetails.booking.status === "string"
            ? JSON.parse(bookingDetails.booking.status)
            : bookingDetails.booking.status;
        onboardingComplete = status?.done === true;
      } catch {
        // Malformed status JSON → treat as not-done → fall through to /onboarding.
      }

      const bookingForState = {
        ...bookingDetails.booking,
        securityStepsComplete: stepsDone,
        // Host display settings ride along with the booking so the main
        // screen knows them at first paint, straight from this fetch.
        guestApp: parseGuestAppConfig(bookingDetails),
      };

      if (stepsDone && onboardingComplete) {
        navigate("/main-v2", {
          replace: autoStart,
          state: {
            room: roomWithTimezone,
            booking: bookingForState,
            manager: manager,
            accessTimes,
          },
        });
      } else {
        navigate("/onboarding", {
          replace: autoStart,
          state: {
            room: roomWithTimezone,
            booking: bookingForState,
            storedStripeId: manager.stripeId,
            manager: manager,
            accessTimes,
          },
        });
      }
    }
  }, [
    loading,
    error,
    manager,
    bookingDetails,
    currentUser.status,
    navigate,
    isLinkExpired,
    upsellID,
    bID,
    autoStart,
  ]);

  return {
    bID,
    manager,
    loading,
    error,
    currentUser,
    bookingDetails,
    handleGetStarted,
    isLinkExpired,
  };
};
